import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { getFormValues, reduxForm } from 'redux-form';
import _ from 'lodash';

// constants
import { showSuccess, showError, showWarning } from '../../Common/alertmessage';
import LocalStrings from '../../../assets/languages';
import { showToast } from '../../Common/alertmessage';
import {
  commonDateFNSFormatter,
  getUserGroupIds,
  maintainResponseStructure,
  replaceAll
} from '../../../util/util-common';
import { ALLOWED_SURVEY_FORM_SOURCES } from '../../../constants/section-constants/source-constant';
import { ALLOWED_GAME_CHALLENGE_TYPES } from '../../../constants/section-constants/gamification-constants';

// actions
import { saveSurvey, fetchSurvey, selectCurrentSurvey } from '../actions';
import {
  fetchGameChallengeStatus,
  setGamificationPopup
} from '../../gamification/actions';

// components
import CheckoutForm from '../../Common/checkout-form';
import EmptyContent from '../../Common/emptyContent';

const MY_FORM_NAME = 'SurveyDataForm';

const SurveyForm = props => {
  const {
    // state
    session,
    deviceSize,
    selectedSurvey,
    checkoutForm,
    formValues,
    groupIds,

    // component props
    source = ALLOWED_SURVEY_FORM_SOURCES.survey,
    refresh = true,

    // redux form
    handleSubmit
  } = props;

  const [loading, setLoading] = useState(false);
  const [dynamicFormLayout, setDynamicFormLayout] = useState([]);
  const [rateObj, setRateObj] = useState([]);
  const [emojiRateObj, setEmojiRateObj] = useState([]);
  const renderSkip = _.includes(
    [ALLOWED_SURVEY_FORM_SOURCES.survey, ALLOWED_SURVEY_FORM_SOURCES.schedule],
    source
  );

  useEffect(() => {
    if (!_.isEmpty(checkoutForm) && formValues) {
      _.map(checkoutForm, field => {
        switch (field?.key) {
          case 'emojirating':
            setEmojiRateObj(prev => [
              ...prev,
              { fieldName: field?.name, value: field?.value }
            ]);
            break;

          case 'rating':
            setRateObj(prev => [
              ...prev,
              { fieldName: field?.name, value: field?.value }
            ]);
            break;

          default:
            break;
        }
      });
    } else {
      setRateObj([]);
      setEmojiRateObj([]);
    }
  }, [formValues, checkoutForm]);

  useEffect(() => {
    handleInitialRender();
  }, [checkoutForm]);

  const onSubmitForm = data => {
    if (loading) return;

    const formErrors = validate(formValues, {
      checkoutForm,
      dynamicFormLayout,
      rateObj,
      emojiRateObj
    });
    if (!_.isEmpty(formErrors)) {
      showWarning(LocalStrings.dynamic.kEnterMandatory);
      return;
    }

    let local_responseArr = [];

    if (checkoutForm.length > 0) {
      let local_checkoutForm = _.cloneDeep(checkoutForm);

      _.map(local_checkoutForm, (formObj, key) => {
        let localObj = formObj;
        const name = formObj.name;

        const dynamicFormItem = _.find(dynamicFormLayout, field =>
          _.isEqual(field?.name, name)
        );

        // === Conditionally hidden form field value clear
        if (!_.isEmpty(dynamicFormItem) && dynamicFormItem?.hideForCondition) {
          if (_.isEqual(localObj.key, 'checkboxes')) {
            localObj.value = [];
          } else localObj.value = '';
        } else if (formObj.key == 'rating') {
          // ------ filter out then same obj from rateObj
          let thisRating = _.find(rateObj, singleRate => {
            return singleRate.fieldName == formObj.name;
          });

          // ----- if has value then push into localObj
          if (!_.isEmpty(thisRating)) {
            localObj.value = thisRating?.value;
          } else localObj.value = '';
        } else if (formObj.key == 'emojirating') {
          // ------ filter out then same obj from emojiRateObj
          let thisRating = _.find(emojiRateObj, singleRate => {
            return singleRate.fieldName == formObj.name;
          });

          // ----- if has value then push into localObj
          if (!_.isEmpty(thisRating)) {
            localObj.value = thisRating?.value;
          } else localObj.value = '';
        } else if (data.hasOwnProperty(name)) {
          if (formObj.key == 'datepicker' && typeof data[name] === 'object') {
            // ----- use of date-fns format
            localObj.value = commonDateFNSFormatter(data[name], 'yyyy-MM-dd');
          } else {
            localObj.value = data[name] ?? '';
          }
        } else {
          // NOTE: should never occur
          if (_.isEqual(localObj.key, 'checkboxes')) localObj.value = [];
          else localObj.value = '';
        }

        // ---- push to response local_responseArr
        local_responseArr.push(localObj);
      });
    }

    let formSubmitObj = {};
    let resubmit = false;
    if (!_.isEmpty(selectedSurvey?.submittedresponse)) {
      resubmit = true;
      formSubmitObj.id = selectedSurvey?.submittedresponse?.id;
      formSubmitObj.eventid = session.eventid;
    } else {
      formSubmitObj = {
        eventid: session.eventid,
        appuserid: session.userid,
        formid: selectedSurvey?.formid,
        formnumber: selectedSurvey?.formnumber
      };
    }
    formSubmitObj.responses = JSON.stringify(
      maintainResponseStructure(local_responseArr)
    );

    setLoading(true);
    props.saveSurvey(session, resubmit, formSubmitObj, response => {
      if (response.success === 0) {
        setLoading(false);

        if (_.isEqual(response?.error?.message, 'reached_max_limit'))
          showToast(LocalStrings.dynamic.kSurveyMaxLimit);
        else showError(LocalStrings.dynamic.kSomethingWentWrongMsg);
      } else {
        showSuccess(
          LocalStrings.dynamic.kSurveyResponseText,
          LocalStrings.dynamic.kThankYou
        );

        props.fetchSurvey(session, { groupIds }, res => {
          props.reset();
          if (refresh) {
            props.selectCurrentSurvey(session, {
              ...selectedSurvey,
              surveytaken: true
            });
          }
        });

        // Gamification Status
        !resubmit &&
          props.fetchGameChallengeStatus(
            session,
            ALLOWED_GAME_CHALLENGE_TYPES.types.survey,
            response => {
              setLoading(false);
              showSuccess(LocalStrings.dynamic.kAnswerSubmittedText);
              if (response.data && response.data.iscompleted == true) {
                if (response.data.showpopup == true) {
                  response.data.challengetype =
                    ALLOWED_GAME_CHALLENGE_TYPES.types.survey;
                  props.setGamificationPopup(true, response.data);
                } else if (response.data?.showpopup) {
                  showToast(
                    `${LocalStrings.dynamic.kYouHaveGot} ${response.data.points} ${LocalStrings.dynamic.kPointsText}`
                  );
                }
              }
            }
          );
      }
    });
  };

  const handleChangeRating = (newRating, name) => {
    setRateObj([
      ..._.filter(rateObj, item => item?.fieldName !== name),
      {
        fieldName: name,
        value: newRating
      }
    ]);
  };

  const handleChangeEmojiRating = (name, value) => {
    setEmojiRateObj([
      ..._.filter(emojiRateObj, item => item?.fieldName !== name),
      {
        fieldName: name,
        value
      }
    ]);
  };

  const handleClearSurvey = () => {
    renderSkip && props.selectCurrentSurvey(null, null);
  };

  // Manages conditional form rendering based on option select
  const handleFormFieldOnChange = e => {
    const fieldName = e.target.name;
    const fieldValue = e.target.value;
    const conditionalForm = selectedSurvey?.conditionalForm;
    let local_dynamicFormLayout = _.cloneDeep(dynamicFormLayout);

    if (!_.isEmpty(fieldValue)) {
      // --- Non-selected other items, to hide
      const nonSelectedOptions = _.chain(conditionalForm?.[fieldName])
        .map((fieldsToHideObj, optionValue) => {
          if (
            (typeof fieldValue === 'object' &&
              !_.includes(fieldValue, optionValue)) || // for checkbox case
            (typeof fieldValue === 'string' &&
              !_.isEqual(optionValue, fieldValue)) // for radio and DD cases
          )
            return optionValue;
        })
        .compact() // removes falsy values
        .value();
      _.map(conditionalForm?.[fieldName], (itemsToHide, opt) => {
        if (_.includes(nonSelectedOptions, opt))
          updateFromField(itemsToHide, true);
      });

      // --- Selected items to render
      _.map(conditionalForm?.[fieldName], (itemsToHide, opt) => {
        if (typeof fieldValue === 'string') {
          if (_.isEqual(fieldValue, opt)) updateFromField(itemsToHide, false);
        } else if (typeof fieldValue === 'object') {
          _.map(fieldValue, selectedValue => {
            if (_.isEqual(selectedValue, opt))
              updateFromField(itemsToHide, false);
          });
        }
      });
    } else {
      _.map(conditionalForm?.[fieldName], itemsToHide => {
        updateFromField(itemsToHide, true);
      });
    }

    // --- Local redux form reset handler
    _.map(local_dynamicFormLayout, fieldItem => {
      if (fieldItem?.hideForCondition) props.autofill(fieldItem?.name, '');
    });
    setDynamicFormLayout(local_dynamicFormLayout);

    // --- This function manages the dynamic form fields based on source mode
    function updateFromField(itemsToHide, isHide = false) {
      // looping conditional form's selected item
      _.map(itemsToHide?.hide, hiddenItem => {
        // looping through it's hide list
        let foundItem = _.find(local_dynamicFormLayout, fItem =>
          _.isEqual(fItem?.name, hiddenItem)
        ); // finding out the dynamic form's field

        // hiding it again based on isHide mode
        if (!_.isEmpty(foundItem)) {
          foundItem.hideForCondition = isHide;

          if (isHide && foundItem?.options) {
            _.map(conditionalForm?.[foundItem?.name], subItemsToHide =>
              updateFromField(subItemsToHide, isHide)
            );
          }
        } else if (!isHide) foundItem.hideForCondition = true; // for isHide mode false, hiding other fields
      });
    }
  };

  const handleInitialRender = () => {
    let local_dynamicFormLayout = _.cloneDeep(checkoutForm);

    if (selectedSurvey?.surveytaken) {
      const conditionalForm = selectedSurvey?.conditionalForm;

      _.map(local_dynamicFormLayout, field => {
        const fieldName = field?.name;
        const fieldValue = field?.value;

        if (field?.options) {
          let hiddenFields = [];
          if (typeof fieldValue === 'string') {
            hiddenFields = [
              ...hiddenFields,
              ..._.filter(
                local_dynamicFormLayout,
                fItem =>
                  conditionalForm?.[fieldName]?.[fieldValue] &&
                  _.includes(
                    conditionalForm?.[fieldName]?.[fieldValue]?.hide,
                    fItem?.name
                  )
              )
            ];
          } else if (typeof hiddenFields === 'object') {
            _.map(fieldValue, fValueItem => {
              hiddenFields = [
                ...hiddenFields,
                ..._.filter(
                  local_dynamicFormLayout,
                  fItem =>
                    conditionalForm?.[fieldName]?.[fValueItem] &&
                    _.includes(
                      conditionalForm?.[fieldName]?.[fValueItem]?.hide,
                      fItem?.name
                    )
                )
              ];
            });
          }

          if (hiddenFields.length) {
            _.map(hiddenFields, fItem => {
              fItem.hideForCondition = false;
            });
          }
        }
      });
    }

    setDynamicFormLayout(local_dynamicFormLayout);
  };

  let style = {
    height: _.includes(
      [
        ALLOWED_SURVEY_FORM_SOURCES.survey,
        ALLOWED_SURVEY_FORM_SOURCES.schedule
      ],
      source
    )
      ? deviceSize.height - 450
      : deviceSize.height - 90
  };
  let formHeight = {
    height: _.isEqual(ALLOWED_SURVEY_FORM_SOURCES.mandatory, source)
      ? deviceSize.height - (deviceSize.isMobile ? 210 : 160) - 143
      : _.includes(
          [
            ALLOWED_SURVEY_FORM_SOURCES.survey,
            ALLOWED_SURVEY_FORM_SOURCES.schedule
          ],
          source
        )
      ? deviceSize.height - (deviceSize.isMobile ? 360 : 310)
      : deviceSize.height - (deviceSize.isMobile ? 210 : 160)
  };

  return (
    <>
      {selectedSurvey?.surveytaken && selectedSurvey?.allowedit != 1 ? (
        <div className="content-body scrollable-content" style={style}>
          <EmptyContent subtitle={LocalStrings.dynamic.kSurveyResponded} />
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmitForm)} className="h-100">
          {/* --- Checkout form --- */}
          <div className="survey-form-container" style={formHeight}>
            <div className="row mx-0 pb-4">
              <div
                className={`${!renderSkip &&
                  'd-none '}col-12 px-0 pb-2 custom-text3-primary`}
              >
                {selectedSurvey?.formname}
              </div>

              <div className="col-12 px-0 custom-text9-normal-secondary">
                {selectedSurvey?.description}
              </div>
            </div>

            <CheckoutForm
              callsForm="survey"
              {...props}
              handleChangeRating={handleChangeRating}
              handleChangeEmojiRating={handleChangeEmojiRating}
              emojiValues={emojiRateObj}
              dynamicForm={dynamicFormLayout}
              onChangeManagerProps={handleFormFieldOnChange}
            />
          </div>

          {/* --- Submit button --- */}
          <hr className="bottombar-divider" />
          <div
            className="bottom-container d-flex align-items-center"
            style={{ gap: '0.5rem' }}
          >
            <button
              type="submit"
              className="custom-button-primary-lg"
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className={`fa fa-refresh fa-spin`}></i>
                  &ensp;
                </>
              ) : (
                ''
              )}
              {LocalStrings.dynamic.kSaveText}
            </button>

            {renderSkip && (
              <span
                className="custom-text6-medium-danger cursor-pointer"
                onClick={handleClearSurvey}
              >
                {LocalStrings.dynamic.kSkip}
              </span>
            )}
          </div>
        </form>
      )}
    </>
  );
};

const validate = (values, ownProps) => {
  let errors = {};

  if (_.isEmpty(values))
    errors['general'] = LocalStrings.dynamic.kEnterMandatory;
  else if (!_.isEmpty(ownProps)) {
    _.map(ownProps?.checkoutForm, (form, index) => {
      const name = form.name;
      const formKey = form.key;

      const dynamicFormItem = _.find(ownProps?.dynamicFormLayout, field =>
        _.isEqual(field?.name, name)
      );

      if (!_.isEmpty(dynamicFormItem) && !dynamicFormItem?.hideForCondition) {
        if (form.required && !form.isreadonly && !form?.hidden) {
          if (formKey === 'rating') {
            if (!_.isEmpty(ownProps?.rateObj)) {
              const selectedVal = _.find(
                ownProps?.rateObj,
                rateItem => rateItem?.fieldName === name
              );
              if (_.isEmpty(selectedVal))
                errors[name] = LocalStrings.dynamic.kFieldRequired;
            } else errors[name] = LocalStrings.dynamic.kFieldRequired;
          } else if (formKey === 'emojirating') {
            if (!_.isEmpty(ownProps?.emojiRateObj)) {
              const selectedVal = _.find(
                ownProps?.emojiRateObj,
                rateItem => rateItem?.fieldName === name
              );
              if (_.isEmpty(selectedVal))
                errors[name] = LocalStrings.dynamic.kFieldRequired;
            } else errors[name] = LocalStrings.dynamic.kFieldRequired;
          } else if (
            !_.isArray(values[name]) &&
            formKey !== 'datepicker' &&
            !values[name] &&
            _.isEmpty(values[name]) // Checking updated
            // (!values[name] || values[name].trim() === '') // caused app crash on 23/06/2023
          ) {
            if (formKey == 'radiobuttons') {
              errors[name] = LocalStrings.dynamic.kFieldRequired;
            } else {
              errors[name] = LocalStrings.dynamic.kFieldRequired;
            }
          }

          if (
            values.hasOwnProperty(name) &&
            _.isArray(values[name]) &&
            values[name]?.length == 0
          ) {
            errors[name] = LocalStrings.dynamic.kFieldRequired;
          }
        }

        if (
          (form?.id === 'facebook' ||
            form?.id === 'twitter' ||
            form?.id === 'linkedin') &&
          values[name] &&
          !validateSocialURL(values[name])
        ) {
          errors[name] = `${_.startCase(_.toLower(form?.id))} ${_.toLower(
            LocalStrings.dynamic.kInvalidUrlText
          )}`;
        }

        if (form.maxlen && values[name] && values[name].length > form.maxlen) {
          errors[name] = replaceAll(
            LocalStrings.dynamic.kTextMaxLengthText,
            '#maxlength',
            form.maxlen
          );
        }

        if (form?.id == 'email' && values[name] && !validateEmail(values[name]))
          errors[name] = LocalStrings.dynamic.kInvalidEmail;
      }
    });
  }

  return errors;
};

const mapStateToProps = state => {
  let initial = {};
  let checkoutForm = state.selectedSurvey?.processedForm;
  _.map(checkoutForm, form => {
    if (!_.isNull(form?.value)) {
      initial[form.name] = form.value;
    }
  });

  return {
    session: state.session,
    deviceSize: state.deviceSize,
    initialValues: initial,
    lastModifiedFormField: state.getLastModifiedFormField,
    formValues: getFormValues(MY_FORM_NAME)(state),
    selectedSurvey: state.selectedSurvey,
    checkoutForm,
    groupIds: getUserGroupIds(state.userDetails)
  };
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      saveSurvey,
      fetchSurvey,
      selectCurrentSurvey,
      fetchGameChallengeStatus,
      setGamificationPopup
    },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: MY_FORM_NAME,
    enableReinitialize: true,
    keepDirtyOnReinitialize: true
  })(SurveyForm)
);
